import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Mail, ArrowRight, RefreshCw } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useLogo } from '../context/LogoContext';
import LanguageSelector from '../components/LanguageSelector';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function VerifyEmailPage() {
  const { t } = useLanguage();
  const { logoUrl } = useLogo();
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [verified, setVerified] = useState(false);

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!email || code.length !== 6) { toast.error(t('verify_code_invalid')); return; }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/verify-email`, { email, code });
      setVerified(true);
      toast.success(t('verify_success'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('verify_failed'));
    } finally { setLoading(false); }
  };

  const handleResend = async () => {
    if (!email) { toast.error(t('verify_enter_email')); return; }
    setResending(true);
    try {
      await axios.post(`${API_URL}/api/auth/resend-code`, { email });
      toast.success(t('verify_resent'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('verify_resend_failed'));
    } finally { setResending(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4 py-8">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-between mb-6">
          <Link to="/" className="flex items-center gap-3">
            <img src={logoUrl} alt="PRC Remit" className="h-8 w-8 rounded-lg" />
            <span className="font-heading font-bold text-xl text-primary">PRC Remit</span>
          </Link>
          <LanguageSelector />
        </div>

        <div className="bg-card border border-border/50 rounded-2xl p-6 sm:p-8" data-testid="verify-email-page">
          {!verified ? (
            <>
              <div className="text-center mb-6">
                <div className="w-14 h-14 bg-accent/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Mail className="h-7 w-7 text-accent" />
                </div>
                <h1 className="font-heading text-xl font-bold text-primary mb-1">{t('verify_title')}</h1>
                <p className="text-sm text-muted-foreground">{t('verify_page_desc')}</p>
              </div>

              <form onSubmit={handleVerify} className="space-y-4">
                <div className="space-y-2">
                  <Label>{t('email')}</Label>
                  <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="user@example.com" required data-testid="verify-page-email" />
                </div>
                <div className="space-y-2">
                  <Label>{t('verify_code_label')}</Label>
                  <Input
                    type="text" inputMode="numeric" maxLength={6}
                    value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    className="text-center text-2xl font-mono tracking-[0.4em] h-14"
                    data-testid="verify-page-code"
                  />
                </div>
                <Button type="submit" className="w-full rounded-full h-12" disabled={loading || code.length !== 6} data-testid="verify-page-submit">
                  {loading ? t('loading') : t('verify_button')}
                </Button>
              </form>

              <div className="mt-6 pt-4 border-t border-border text-center">
                <p className="text-xs text-muted-foreground mb-3">{t('verify_no_code')}</p>
                <Button variant="ghost" size="sm" onClick={handleResend} disabled={resending} className="gap-2" data-testid="verify-page-resend">
                  <RefreshCw className={`h-3.5 w-3.5 ${resending ? 'animate-spin' : ''}`} />
                  {resending ? t('loading') : t('verify_resend')}
                </Button>
              </div>
            </>
          ) : (
            <div className="text-center py-4" data-testid="verify-page-done">
              <div className="w-16 h-16 bg-emerald-500/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <svg className="h-8 w-8 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
              </div>
              <h2 className="font-heading text-xl font-bold text-primary mb-2">{t('verify_complete_title')}</h2>
              <p className="text-sm text-muted-foreground mb-6">{t('verify_complete_desc')}</p>
              <Link to="/login">
                <Button className="rounded-full h-12 px-8 gap-2" data-testid="verify-go-login">
                  {t('login')} <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
            </div>
          )}

          <p className="text-center text-sm text-muted-foreground mt-6">
            <Link to="/login" className="text-accent font-medium hover:underline">{t('login')}</Link>
            <span className="mx-2">|</span>
            <Link to="/register" className="text-accent font-medium hover:underline">{t('register_btn')}</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
